import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight, Sparkles, Loader2, Send, Clock, Plus,
  Users, Calculator, Layers, Cpu, Shield, AlertTriangle, TrendingUp,
  ShoppingBag, Megaphone, ShoppingCart, Factory, Truck, Settings,
  FolderKanban, Building2, Scale, Crown, Server, LayoutGrid, Gavel, CheckCircle2,
  ClipboardCheck, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { ReportHistory } from "./SheetsHub";
import MyTasksBoard from "../../components/os/MyTasksBoard";
import CashflowBlock from "../../components/os/CashflowBlock";
import SupplierQuotesInbox from "../../components/os/SupplierQuotesInbox";
import { AiBlock } from "../../components/os/AiBlocks";
import { api } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";
import { useAuth } from "../../context/AuthContext";

const ease = [0.16, 1, 0.3, 1];
const LEADERSHIP = ["admin", "ceo", "director"];

const SUITE_ICONS = {
  Users, Calculator, Layers, Cpu, Shield, AlertTriangle, Sparkles, TrendingUp,
  ShoppingBag, Megaphone, ShoppingCart, Factory, Truck, Settings, FolderKanban,
  Building2, Scale, Crown, Server,
};
const TONE = {
  violet: "text-violet", emerald: "text-emerald-600", amber: "text-amber-500", navy: "text-navy/70",
};
const fmtMoney = (v) =>
  "$" + Number(v || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 });

function CopilotCard({ rd = false }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [thread, setThread] = useState([]);
  const ask = async (text) => {
    const question = (text ?? q).trim();
    if (!question || busy) return;
    setQ("");
    setBusy(true);
    const history = thread.flatMap((t) => [
      { role: "user", content: t.q },
      { role: "assistant", content: t.answer || "" },
    ]).slice(-10);
    try {
      const { data } = await api.post("/ai/copilot", { question, history });
      setThread((th) => [...th, { q: question, answer: data.answer, blocks: data.blocks || [] }]);
    } catch (e) {
      setThread((th) => [...th, { q: question, answer: e?.response?.data?.detail || "No se pudo consultar al Copiloto.", blocks: [] }]);
    } finally { setBusy(false); }
  };
  const suggestions = rd ? [
    "¿Cómo está mi flotilla y qué documentos faltan?",
    "¿Cómo van mis planes de Planning?",
    "Resume mis hojas de Google Sheets",
  ] : [
    "Dame el dashboard de tesorería",
    "¿Cómo va el pipeline del CRM?",
    "¿Qué facturas están pendientes de cobro?",
  ];
  const renderAnswer = (text) =>
    (text || "").split("\n").map((line, i) => {
      const html = line.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/^#+\s*/, "");
      if (!line.trim()) return <div key={i} className="h-2" />;
      return <div key={i} dangerouslySetInnerHTML={{ __html: html }} />;
    });
  return (
    <div className="rounded-xl border border-violet/25 bg-gradient-to-br from-violet/[0.06] to-transparent p-6" data-testid="hub-copilot">
      <div className="flex items-center gap-2 text-violet mb-3">
        <Sparkles className="w-4 h-4" />
        <span className="overline">Copiloto OMNIA</span>
        <span className="ml-auto flex items-center gap-1.5 text-[11px] text-emerald-600">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Conectado en vivo
        </span>
        {thread.length > 0 && (
          <button onClick={() => setThread([])} data-testid="hub-copilot-clear"
            className="text-[11px] text-navy/40 hover:text-navy underline underline-offset-2">Limpiar</button>
        )}
      </div>
      {thread.length > 0 && (
        <div className="mb-4 space-y-4 max-h-[28rem] overflow-y-auto thin-scroll pr-1" data-testid="hub-copilot-answer">
          {thread.map((t, ti) => (
            <div key={ti} className="space-y-2">
              <div className="flex justify-end">
                <span className="inline-block rounded-xl rounded-br-sm bg-navy text-cream text-xs px-3 py-1.5 max-w-[85%]">{t.q}</span>
              </div>
              {t.answer && <div className="text-sm text-navy/75 leading-relaxed space-y-0.5">{renderAnswer(t.answer)}</div>}
              {(t.blocks || []).map((b, i) => (
                <AiBlock key={i} block={b} onSend={(v) => ask(v)} disabled={busy} />
              ))}
            </div>
          ))}
        </div>
      )}
      {busy && (
        <div className="mb-3 flex items-center gap-2 text-sm text-violet/70">
          <Loader2 className="w-4 h-4 animate-spin" /> Consultando tus módulos en vivo…
        </div>
      )}
      <div className="flex gap-2">
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && ask()}
          placeholder={thread.length ? "Pregunta de seguimiento…" : "Pregúntale a OMNIA sobre tu negocio…"}
          data-testid="hub-copilot-input"
          className="field flex-1" />
        <button onClick={() => ask()} disabled={busy} className="btn btn-primary shrink-0" data-testid="hub-copilot-send">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
      {thread.length === 0 && !busy && (
        <div className="mt-3 flex flex-wrap gap-2">
          {suggestions.map((s) => (
            <button key={s} onClick={() => ask(s)} className="text-xs rounded-full border border-navy/15 px-3 py-1 text-navy/50 hover:border-violet hover:text-violet transition-colors">{s}</button>
          ))}
        </div>
      )}
    </div>
  );
}

const fmtWhen = (iso) => {
  if (!iso) return "Sin detecciones";
  try {
    return new Date(iso).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso.slice(0, 16); }
};

function SheetsReportsPanel() {
  return (
    <div className="rounded-xl border border-navy/10 bg-white p-6" data-testid="hub-sheets-reports">
      <div className="flex items-center gap-2 mb-4">
        <LayoutGrid className="w-4 h-4 text-violet" />
        <h3 className="font-medium text-sm">Informes de tus hojas</h3>
        <Link to="/os/sheets" className="ml-auto text-xs text-violet hover:underline" data-testid="hub-sheets-reports-all">Ver todos →</Link>
      </div>
      <ReportHistory compact />
    </div>
  );
}

function AlertsPanel() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/ai/alerts/panel").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;
  const { rules = [], system = [], digest } = data;
  return (
    <div className="rounded-xl border border-navy/10 bg-white p-6" data-testid="hub-alerts-panel">
      <div className="flex items-center gap-2 mb-4">
        <span className="overline text-navy/40">Alertas de OMNIA</span>
        <span className="ml-auto text-[11px] text-navy/35">{rules.length} personalizada(s)</span>
      </div>
      <div className="space-y-2">
        {system.map((s) => (
          <div key={s.key} className="flex items-center gap-3 rounded-lg border border-navy/5 bg-cream/60 px-3 py-2" data-testid={`hub-alert-system-${s.key}`}>
            <span className={`w-2 h-2 rounded-full shrink-0 ${s.last_triggered ? "bg-rose-500" : "bg-emerald-500"}`} />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-navy/80 truncate">{s.label}</div>
              <div className="text-[11px] text-navy/40">Última detección: {fmtWhen(s.last_triggered)}</div>
            </div>
            <span className="text-[10px] uppercase tracking-wider text-navy/30">Sistema</span>
          </div>
        ))}
        {rules.map((r) => (
          <div key={r.id} className="flex items-center gap-3 rounded-lg border border-violet/15 bg-violet/[0.04] px-3 py-2" data-testid={`hub-alert-rule-${r.id}`}>
            <span className={`w-2 h-2 rounded-full shrink-0 ${r.last_triggered ? "bg-[#C9A96A]" : "bg-navy/20"}`} />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-navy/80 truncate">{r.name}</div>
              <div className="text-[11px] text-navy/40 truncate">
                {r.module} · {(r.filters || []).map((f) => `${f.field} ${f.op || "="} ${f.value}`).join("; ")} · Última: {fmtWhen(r.last_triggered)}{r.hits ? ` · ${r.hits} detección(es)` : ""}
              </div>
            </div>
            <span className="text-[10px] uppercase tracking-wider text-violet/60">Mía</span>
          </div>
        ))}
        {rules.length === 0 && (
          <div className="text-xs text-navy/40 px-1">Pídele a OMNIA en el chat: “avísame cuando…” para crear tus propias alertas.</div>
        )}
        {digest && (
          <div className="flex items-center gap-3 rounded-lg border border-navy/5 px-3 py-2 mt-1" data-testid="hub-alert-digest">
            <span className={`w-2 h-2 rounded-full shrink-0 ${digest.enabled ? "bg-emerald-500" : "bg-navy/20"}`} />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-navy/80">Resumen ejecutivo diario · {digest.time} h</div>
              <div className="text-[11px] text-navy/40 truncate">
                Destinatarios: {Array.isArray(digest.recipients) ? digest.recipients.join(", ") : digest.recipients} · Último envío: {fmtWhen(digest.last_sent)}
              </div>
            </div>
            <span className="text-[10px] uppercase tracking-wider text-navy/30">{digest.enabled ? "Activo" : "Pausado"}</span>
          </div>
        )}
      </div>
    </div>
  );
}

const KIND_LABELS = {
  vacaciones: "Vacaciones", permiso: "Permiso", incapacidad: "Incapacidad", personal: "Personal",
  maternidad_paternidad: "Maternidad/Paternidad", gasto: "Gasto", documento: "Documento",
  cambio_puesto: "Cambio de puesto", cambio_salario: "Cambio salarial", parte_horas: "Horas",
};

function ApprovalsInbox() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/approvals/inbox").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const decide = async (it, approve) => {
    setBusy(it.id);
    try {
      if (it.kind === "timeoff") await api.patch(`/people/timeoff/${it.id}`, { status: approve ? "approved" : "rejected" });
      else if (it.kind === "request") await api.post(`/approvals/requests/${it.id}/decide`, { action: approve ? "aprobar" : "rechazar" });
      else await api.patch(`/workforce/timesheets/${it.id}`, { status: approve ? "aprobado" : "rechazado" });
      toast.success(approve ? "Aprobado · el solicitante fue notificado" : "Rechazado · el solicitante fue notificado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    setBusy(null);
  };
  return (
    <div className="rounded-xl border border-emerald-600/25 bg-gradient-to-br from-emerald-500/[0.05] to-transparent p-6" data-testid="hub-approvals-inbox">
      <div className="flex items-center gap-2 mb-4">
        <ClipboardCheck className="w-4 h-4 text-emerald-700" />
        <span className="overline text-emerald-800">Bandeja de autorizaciones</span>
        <span className="ml-auto rounded-full bg-white border border-navy/10 px-2.5 py-0.5 text-[11px] text-navy/55" data-testid="hub-approvals-count">
          {data.count} pendiente(s)
        </span>
      </div>
      {data.count === 0 ? (
        <div className="text-xs text-navy/40 px-1" data-testid="hub-approvals-empty">
          Estás al día: no tienes vacaciones, gastos, documentos ni partes de horas por autorizar.
        </div>
      ) : (
        <div className="space-y-2">
          {data.items.map((it) => (
            <div key={`${it.kind}-${it.id}`} className="flex items-center gap-3 rounded-lg border border-navy/8 bg-white px-4 py-3"
              data-testid={`approval-item-${it.id}`}>
              <span className="shrink-0 rounded-full bg-navy/5 px-2.5 py-1 text-[10px] uppercase tracking-wider text-navy/55">
                {KIND_LABELS[it.tipo] || it.tipo}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-navy/85 truncate">{it.title}</div>
                <div className="text-[11px] text-navy/40 truncate">{it.who} · {it.detail}</div>
              </div>
              <button onClick={() => decide(it, true)} disabled={busy === it.id} data-testid={`approval-approve-${it.id}`}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-[11px] text-emerald-700 hover:bg-emerald-100 transition-colors disabled:opacity-50">
                {busy === it.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Aprobar
              </button>
              <button onClick={() => decide(it, false)} disabled={busy === it.id} data-testid={`approval-reject-${it.id}`}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 text-[11px] text-rose-600 hover:bg-rose-100 transition-colors disabled:opacity-50">
                <XCircle className="w-3.5 h-3.5" /> Rechazar
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CouncilPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/ai/council/panel").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const { open = [], stats = {} } = data;
  const mark = async (id) => {
    setBusy(id);
    try { await api.patch(`/ai/council/decisions/${id}`, { status: "done" }); load(); } catch { /* */ }
    setBusy(null);
  };
  return (
    <div className="rounded-xl border border-[#C9A96A]/35 bg-gradient-to-br from-[#C9A96A]/[0.06] to-transparent p-6" data-testid="hub-council-panel">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Gavel className="w-4 h-4 text-[#8a6d34]" />
        <span className="overline text-[#8a6d34]">Consejo Directivo · Decisiones</span>
        <div className="ml-auto flex items-center gap-2 text-[11px]">
          <span className="rounded-full bg-white border border-navy/10 px-2.5 py-0.5 text-navy/55">{stats.open ?? 0} abierta(s)</span>
          {stats.overdue > 0 && <span className="rounded-full bg-rose-50 border border-rose-200 px-2.5 py-0.5 text-rose-600" data-testid="hub-council-overdue">{stats.overdue} vencida(s)</span>}
          <span className="rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 text-emerald-700">{stats.done_total ?? 0} cumplida(s)</span>
        </div>
      </div>
      {open.length === 0 ? (
        <div className="text-xs text-navy/40 px-1" data-testid="hub-council-empty">
          No hay decisiones abiertas. Plantéale al Consejo un tema estratégico en el chat de OMNIA y la decisión del CEO quedará registrada aquí.
        </div>
      ) : (
        <div className="space-y-2">
          {open.map((d) => (
            <div key={d.id} className="rounded-lg border border-navy/8 bg-white px-4 py-3" data-testid={`hub-council-decision-${d.id}`}>
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-navy/85 leading-snug">{d.decision}</div>
                  {d.situation && <div className="mt-0.5 text-[11px] text-navy/40 truncate">{d.situation}</div>}
                  <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
                    <span className="rounded-full bg-navy/5 px-2.5 py-0.5 text-navy/60">Responsable: {d.responsible || "—"}</span>
                    <span className={`rounded-full px-2.5 py-0.5 ${d.overdue ? "bg-rose-50 border border-rose-200 text-rose-600 font-medium" : "bg-navy/5 text-navy/60"}`}>
                      Plazo: {d.due_date || "—"}{d.overdue ? " · VENCIDA" : ""}
                    </span>
                    {(d.kpis || []).slice(0, 2).map((k, i) => (
                      <span key={i} className="rounded-full bg-navy/5 px-2.5 py-0.5 text-navy/45">KPI · {k}</span>
                    ))}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="h-1.5 w-36 rounded-full bg-navy/8 overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-[#C9A96A] to-emerald-500 transition-all"
                        style={{ width: d.tasks_total ? `${Math.round((d.tasks_done / d.tasks_total) * 100)}%` : "0%" }} />
                    </div>
                    <span className="text-[11px] text-navy/45" data-testid={`hub-council-progress-${d.id}`}>
                      {d.tasks_total ? `${d.tasks_done}/${d.tasks_total} tareas` : "sin tareas vinculadas"}
                    </span>
                    {(d.follow_ups || []).length > 0 && (
                      <span className="text-[11px] text-[#8a6d34]">· {d.follow_ups.length} seguimiento(s) del CEO</span>
                    )}
                  </div>
                </div>
                <button onClick={() => mark(d.id)} disabled={busy === d.id} data-testid={`hub-council-done-${d.id}`}
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-[11px] text-emerald-700 hover:bg-emerald-100 transition-colors disabled:opacity-50">
                  {busy === d.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Marcar cumplida
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RDHub({ data, t }) {
  const [fleet, setFleet] = useState(null);
  const [plans, setPlans] = useState(null);
  const [sheets, setSheets] = useState(null);
  const [crews, setCrews] = useState(null);

  useEffect(() => {
    api.get("/fleet/units").then((r) => setFleet(r.data)).catch(() => setFleet([]));
    api.get("/planning/plans").then((r) => setPlans(r.data)).catch(() => setPlans([]));
    api.get("/sheets").then((r) => setSheets(Array.isArray(r.data) ? r.data : r.data?.items || [])).catch(() => setSheets([]));
    api.get("/cuadrillas/summary").then((r) => setCrews(r.data)).catch(() => setCrews({ total: 0 }));
  }, []);

  const vencProximos = (fleet || []).reduce((n, u) => {
    const today = new Date();
    return n + ["seguro_vence", "verificacion_vence", "tenencia_vence", "licencia_vence"].filter((f) => {
      if (!u[f]) return false;
      const d = (new Date(u[f]) - today) / 86400000;
      return d <= 30;
    }).length;
  }, 0);

  const cards = [
    { to: "/os/cuadrillas", icon: Users, title: "OMNIA Cuadrillas", testid: "rd-card-cuadrillas",
      value: crews === null ? "…" : crews.total || 0, label: crews?.total === 1 ? "cuadrilla" : "cuadrillas",
      extra: crews === null ? "" : crews.total ? `${crews.disponibles || 0} disponibles · ${crews.ocupadas || 0} ocupadas · ${crews.inactivas || 0} inactivas` : "Registra tu primera cuadrilla" },
    { to: "/os/flotilla", icon: Truck, title: "OMNIA Flotilla (Strix)", testid: "rd-card-flotilla",
      value: fleet === null ? "…" : fleet.length, label: fleet?.length === 1 ? "unidad registrada" : "unidades registradas",
      extra: fleet === null ? "" : vencProximos > 0 ? `⚠ ${vencProximos} vencimiento(s) en 30 días o vencidos` : "Sin vencimientos próximos" },
    { to: "/os/planning", icon: FolderKanban, title: "OMNIA Planning", testid: "rd-card-planning",
      value: plans === null ? "…" : plans.length, label: plans?.length === 1 ? "plan" : "planes",
      extra: plans === null ? "" : plans.slice(0, 2).map((p) => `${p.nombre} · ${p.estado}`).join("  |  ") || "Crea tu primer plan" },
    { to: "/os/sheets", icon: LayoutGrid, title: "OMNIA Documents Hub", testid: "rd-card-sheets",
      value: sheets === null ? "…" : sheets.length, label: sheets?.length === 1 ? "hoja conectada" : "hojas conectadas",
      extra: sheets === null ? "" : sheets.length ? "Informes y alertas activos" : "Conecta tu primera Google Sheet" },
  ];

  return (
    <OSLayout title={t("os.titles.hub")}>
      {!data ? (
        <div className="text-navy/40">{t("os.loading")}</div>
      ) : (
        <div className="space-y-8 p425" data-testid="rd-hub">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
            <div className="overline text-navy/40 mb-2">Usuario RD</div>
            <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">
              {t("os.hub.hello")}, {data.greeting_name?.split(" ")[0]}.
            </h2>
            <p className="mt-2 text-navy/50">Tu flotilla, tu planificación y tus hojas — en un solo lugar.</p>
          </motion.div>

          <CopilotCard rd />

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="rd-modules">
            {cards.map((c, i) => (
              <motion.div key={c.to} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease, delay: i * 0.06 }}>
                <Link to={c.to} data-testid={c.testid}
                  className="block bg-white border border-navy/10 rounded-xl p-6 hover:border-violet/40 hover:shadow-sm transition-all group h-full">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-violet/10 flex items-center justify-center text-violet shrink-0">
                      <c.icon className="w-5 h-5" />
                    </div>
                    <div className="text-sm font-medium text-navy/80">{c.title}</div>
                    <ArrowUpRight className="w-4 h-4 text-navy/20 group-hover:text-violet transition-colors ml-auto shrink-0" />
                  </div>
                  <div className="mt-4 text-3xl font-medium tracking-tight">{c.value}</div>
                  <div className="text-xs text-navy/40">{c.label}</div>
                  <div className="mt-3 text-xs text-navy/50 truncate">{c.extra}</div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </OSLayout>
  );
}

export default function Hub() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [overview, setOverview] = useState(null);
  const [suites, setSuites] = useState([]);
  const [recents, setRecents] = useState([]);
  const isLeader = LEADERSHIP.includes(user?.role);
  const isRD = user?.role === "usuario_rd";

  useEffect(() => {
    api.get("/hub/summary").then((r) => setData(r.data)).catch(() => {});
    if (!isRD) {
      api.get("/hub/overview").then((r) => setOverview(r.data)).catch(() => {});
      api.get("/catalog").then((r) => setSuites(r.data?.suites || [])).catch(() => {});
      try { setRecents(JSON.parse(localStorage.getItem("omnia_cmd_recent") || "[]")); } catch { /* noop */ }
    }
  }, [isRD]);

  if (isRD) return <RDHub data={data} t={t} />;

  const suiteCount = (s) => s.studios.reduce((n, st) => n + st.apps.length, 0);
  const suiteFirstRoute = (s) => {
    for (const st of s.studios) for (const a of st.apps) if (a.route) return a.route;
    return "/os";
  };

  return (
    <OSLayout title={t("os.titles.hub")}>
      {!data ? (
        <div className="text-navy/40">{t("os.loading")}</div>
      ) : (
        <div className="space-y-10">
          {/* Header */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
            <div className="overline text-navy/40 mb-2">{data.role_label}</div>
            <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">
              {t("os.hub.hello")}, {data.greeting_name?.split(" ")[0]}.
            </h2>
            <p className="mt-2 text-navy/50">{t("os.hub.subtitle")}</p>
          </motion.div>

          {/* Copiloto (solo liderazgo) */}
          {isLeader && <CopilotCard />}
          {isLeader && <ApprovalsInbox />}
          {isLeader && <CouncilPanel />}
          {isLeader && <AlertsPanel />}
          <SheetsReportsPanel />

          {/* KPIs ejecutivos en vivo (cross-suite) */}
          <div>
            <div className="overline text-navy/40 mb-3">Panel ejecutivo · en vivo</div>
            {!overview ? (
              <div className="text-navy/30 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Calculando indicadores…</div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {overview.kpis.map((k, i) => (
                  <motion.button
                    key={k.label} onClick={() => navigate(k.to)}
                    initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, ease, delay: i * 0.04 }}
                    data-testid={`hub-exec-kpi-${i}`}
                    className="text-left bg-white border border-navy/10 rounded-lg p-5 hover:border-violet/40 hover:shadow-sm transition-all group">
                    <div className="flex items-start justify-between">
                      <div className="overline text-navy/40">{k.label}</div>
                      <ArrowUpRight className="w-3.5 h-3.5 text-navy/20 group-hover:text-violet transition-colors" />
                    </div>
                    <div className={`mt-3 text-2xl font-medium tracking-tight ${TONE[k.tone] || "text-navy"}`}>
                      {k.money ? fmtMoney(k.value) : k.value}
                    </div>
                  </motion.button>
                ))}
              </div>
            )}
          </div>

          {/* Accesos rápidos + Recientes */}
          {(data.quick_actions?.length > 0 || recents.length > 0) && (
            <div className="space-y-4">
              {data.quick_actions?.length > 0 && (
                <div className="flex flex-wrap gap-3" data-testid="hub-quick-actions">
                  {data.quick_actions.map((qa) => (
                    <Link key={qa.to} to={qa.to} data-testid={`hub-qa-${qa.to.split("/").pop()}`}
                      className="group inline-flex items-center gap-2 rounded-full border border-navy/15 px-4 py-2 text-sm hover:border-violet hover:text-violet transition-colors">
                      <Plus className="w-3.5 h-3.5" /> {qa.label}
                    </Link>
                  ))}
                </div>
              )}
              {recents.length > 0 && (
                <div className="flex flex-wrap items-center gap-2" data-testid="hub-recents">
                  <span className="inline-flex items-center gap-1 text-xs text-navy/35"><Clock className="w-3.5 h-3.5" /> Recientes:</span>
                  {recents.map((r) => (
                    <Link key={r.link} to={r.link} className="text-xs rounded-full bg-navy/[0.04] px-3 py-1 text-navy/60 hover:text-violet transition-colors">{r.label}</Link>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Mapa de Suites y Apps */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="overline text-navy/40">Suites · {suites.length} · {suites.reduce((n, s) => n + suiteCount(s), 0)} apps</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="hub-suites">
              {suites.map((s, i) => {
                const Icon = SUITE_ICONS[s.icon] || LayoutGrid;
                return (
                  <motion.button
                    key={s.id} onClick={() => navigate(suiteFirstRoute(s))}
                    initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, ease, delay: i * 0.03 }}
                    data-testid={`hub-suite-${s.id}`}
                    className="text-left bg-white border border-navy/10 rounded-lg p-5 hover:border-violet/40 hover:shadow-sm transition-all group">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-violet/10 flex items-center justify-center text-violet shrink-0">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-navy/80 truncate">{s.name}</div>
                        <div className="text-xs text-navy/40">{suiteCount(s)} apps</div>
                      </div>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </div>

          <MyTasksBoard />
          <SupplierQuotesInbox />
          <CashflowBlock />

          {/* Actividad organizacional en vivo (solo liderazgo) */}
          {overview?.is_leader && (
          <div className="bg-white border border-navy/10 rounded-lg p-6">
            <div className="overline text-navy/40 mb-5">{t("os.hub.activityTitle")}</div>
            <div className="space-y-5" data-testid="hub-activity">
              {(overview.activity.length === 0) ? (
                <p className="text-sm text-navy/35" data-testid="hub-activity-empty">{t("os.hub.activityEmpty") || "Aún no hay actividad. Aparecerá aquí a medida que uses OMNIA OS."}</p>
              ) : overview.activity.map((a, i) => (
                <div key={i} className="flex gap-3">
                  <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gradient-violet shrink-0" />
                  <div>
                    <p className="text-sm text-navy/70 leading-snug">{a.text}</p>
                    <p className="text-xs text-navy/35 mt-1">{a.when}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}
        </div>
      )}
    </OSLayout>
  );
}
